// ============================================
// APPLICATION - Fiados Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fiadosRepository } from '../../infrastructure/http/fiados.repository';
import { queryKeys } from '../query-keys';
import type { FiltrosFiadosPendientes, FiltrosFiadosHistorico, FiadosHistoricoResponse, PagarFiadoRequest, DetalleFiadosClienteResponse, ReasignarFiadoRequest } from '../../domain/dtos';

/**
 * Hook para obtener fiados pendientes
 */
export const useFiadosPendientes = (filtros?: FiltrosFiadosPendientes) => {
  return useQuery({
    queryKey: [...queryKeys.fiados.pendientes, filtros],
    queryFn: () => fiadosRepository.obtenerFiadosPendientes(filtros),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para obtener histórico de fiados
 * Requiere fechas obligatorias
 */
export const useFiadosHistorico = (filtros: FiltrosFiadosHistorico, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...queryKeys.fiados.historico, filtros],
    queryFn: () => fiadosRepository.obtenerFiadosHistorico(filtros),
    enabled: enabled && !!filtros.fechaDesde && !!filtros.fechaHasta,
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para eliminar un fiado
 * Implementa optimistic update para actualizar la UI sin refetch
 */
export const useEliminarFiado = (filtros: FiltrosFiadosHistorico) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fiadoId: number) => fiadosRepository.eliminarFiado(fiadoId),
    onSuccess: (_, fiadoId) => {
      // Optimistic update: actualizar cache sin refetch
      const queryKey = [...queryKeys.fiados.historico, filtros];
      
      queryClient.setQueryData<FiadosHistoricoResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          content: oldData.content.filter(fiado => fiado.fiadoId !== fiadoId),
          totalElements: oldData.totalElements - 1,
        };
      });
    },
  });
};

/**
 * Hook para pagar fiados
 * Invalida queries después de pagar exitosamente
 */
export const usePagarFiados = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PagarFiadoRequest) => fiadosRepository.pagarFiados(request),
    onSuccess: () => {
      // queryKeys.fiados.all == ['fiados'], cubre pendientes/historico y sus sub-keys de
      // detalle/totales por clienteAlmacenId (antes se invalidaba solo pendientes+historico,
      // dejando los totales del cliente desactualizados hasta un refetch manual).
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.all });
    },
  });
};

/**
 * Hook para obtener detalle de fiados de un cliente
 */
export const useDetalleFiadosCliente = (clienteAlmacenId: number, page: number = 0) => {
  return useQuery({
    queryKey: [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId, page],
    queryFn: () => fiadosRepository.obtenerDetalleFiadosCliente(clienteAlmacenId, page),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para reasignar un fiado a otro cliente del mismo almacén
 */
export const useReasignarFiado = (clienteAlmacenId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fiadoId, request }: { fiadoId: number; request: ReasignarFiadoRequest }) =>
      fiadosRepository.reasignarFiado(fiadoId, request),
    onSuccess: () => {
      // Invalida por prefijo (sin page) para refrescar todas las páginas en caché del detalle
      queryClient.invalidateQueries({ queryKey: [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.pendientes });
    },
  });
};

/**
 * Hook para obtener totales globales de fiados pendientes de un cliente
 */
export const useTotalesFiadosCliente = (clienteAlmacenId: number) => {
  return useQuery({
    queryKey: [...queryKeys.fiados.pendientes, 'totales', clienteAlmacenId],
    queryFn: () => fiadosRepository.obtenerTotalesFiadosCliente(clienteAlmacenId),
    gcTime: 5 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para obtener el historial de abonos de un fiado
 */
export const useAbonosFiado = (fiadoId: number | null) => {
  return useQuery({
    queryKey: [...queryKeys.fiados.pendientes, 'abonos', fiadoId],
    queryFn: () => fiadosRepository.obtenerAbonosFiado(fiadoId!),
    enabled: fiadoId != null,
    gcTime: 5 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para pagar todos los fiados pendientes de un cliente en una sola acción
 */
export const usePagarTodoFiados = (clienteAlmacenId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tipoPago: string) =>
      fiadosRepository.pagarTodoFiadosCliente(clienteAlmacenId, tipoPago),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.fiados.pendientes, 'totales', clienteAlmacenId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.pendientes });
    },
  });
};

/**
 * Hook para eliminar un fiado desde el detalle del cliente
 */
export const useEliminarFiadoDetalle = (clienteAlmacenId: number, page: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fiadoId: number) => fiadosRepository.eliminarFiado(fiadoId),
    onSuccess: (_, fiadoId) => {
      // Optimistic update en el detalle
      const queryKey = [...queryKeys.fiados.pendientes, 'detalle', clienteAlmacenId, page];
      
      queryClient.setQueryData<DetalleFiadosClienteResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          content: oldData.content.filter(fiado => fiado.id !== fiadoId),
          totalElements: oldData.totalElements - 1,
        };
      });

      // También invalidar las otras queries
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.pendientes });
    },
  });
};
